<template>
  <div
    id="app"
    ref="appShellRef"
    class="app-theme-canvas min-h-[var(--app-height)] sm:h-[var(--app-height)] flex flex-col mx-auto relative overflow-visible sm:overflow-hidden max-w-4xl transition-[max-width] duration-200"
  >
    <!-- Header with SyncIndicator & UserMenu -->
    <AppHeader
      :current-deck="currentDeck"
      @open-preferences="isPreferencesOpen = true"
      @open-login="isLoginOpen = true"
    />

    <!-- Non-blocking background job toasts -->
    <ToastNotificationStack :toasts="toastAlerts" @dismiss="dismissToast" />

    <!-- Central Flashcard Stage -->
    <main
      ref="mainColumnRef"
      class="min-w-0 flex-1 max-w-4xl overflow-visible sm:overflow-y-auto relative scroll-smooth flex flex-col items-center justify-center p-3 sm:p-6 pb-24 sm:pb-28"
    >
      <FlashCardCanvas
        :card="activeCard"
        @submit-reply="handleTextSubmit"
        @word-action="onWordAction"
      />
    </main>

    <!-- Bottom Action Dock (matches TaskManager floating dock) -->
    <FloatingBottomDock
      :buttons="currentButtons"
      :is-loading="isLoadingAction"
      @button-click="onDockButtonClick"
    />


    <!-- Contextual Input Drawer / Modal for Add Words & file attachments -->
    <ContextualInputDrawer
      v-model:open="isDrawerOpen"
      :prompt-text="drawerPrompt"
      :is-submitting="isLoadingAction"
      @submit-text="handleTextSubmit"
      @submit-file="handleFileUpload"
    />

    <!-- Preferences Modal (Learning, Appearance, Telegram, Storage) -->
    <PreferencesModal
      v-if="isPreferencesOpen"
      @close="isPreferencesOpen = false"
      @filter-action="handleFilterAction"
      @open-login="isLoginOpen = true"
      @preferences-saved="onPreferencesSaved"
    />

    <!-- Word Lists Management Modal -->
    <WordListModal
      :open="isWordListOpen"
      :initial-filter="wordListInitialFilter"
      @close="isWordListOpen = false"
      @word-restored="initSession"
    />

    <!-- Telegram Dual Login Modal -->
    <LoginModal v-model:open="isLoginOpen" @login-success="onLoginSuccess" />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AppHeader from './components/AppHeader.vue';
import FlashCardCanvas from './components/FlashCardCanvas.vue';
import FloatingBottomDock from './components/FloatingBottomDock.vue';
import ContextualInputDrawer from './components/ContextualInputDrawer.vue';
import ToastNotificationStack from './components/ToastNotificationStack.vue';
import PreferencesModal from './components/PreferencesModal.vue';
import WordListModal from './components/WordListModal.vue';
import LoginModal from './components/LoginModal.vue';

import { useTheme } from './composables/useTheme';
import { useAuth } from './composables/useAuth';
import { useLearningSession } from './composables/useLearningSession';
import type { CardData, OutboundButton } from './types';

const { initializeTheme } = useTheme();
const { currentUser, isLoggedIn, checkAuth } = useAuth();
const {
  activeCard,
  currentButtons,
  toastAlerts,
  isDrawerOpen,
  drawerPrompt,
  isLoadingAction,
  initSession,
  presentNextLocalCard,
  handleButtonClick,
  handleTextSubmit,
  handleFileUpload,
  dismissToast,
} = useLearningSession();

const isLoginOpen = ref(false);
const isPreferencesOpen = ref(false);
const isWordListOpen = ref(false);
const wordListInitialFilter = ref('active');
const currentDeck = ref<string>('');

const appShellRef = ref<HTMLElement | null>(null);
const mainColumnRef = ref<HTMLElement | null>(null);
let mainColumnObserver: ResizeObserver | undefined;

const updateMainColumnFrame = () => {
  const rect = mainColumnRef.value?.getBoundingClientRect();
  if (!rect) return;
  document.documentElement.style.setProperty('--main-column-left', `${rect.left}px`);
  document.documentElement.style.setProperty('--main-column-width', `${rect.width}px`);
};

const onLoginSuccess = () => {
  initSession();
};

const onPreferencesSaved = () => {
  void presentNextLocalCard({ deterministicFirst: true });
};

const handleFilterAction = (filterKind: string) => {
  wordListInitialFilter.value = filterKind;
  isWordListOpen.value = true;
};

const onDockButtonClick = (btn: OutboundButton) => {
  const cb = (btn.callback_data || '').trim().toLowerCase();
  const cap = (btn.caption || '').trim().toLowerCase();
  if (cb.includes('open_dict') || cap.includes('dictionary')) {
    wordListInitialFilter.value = 'active';
    isWordListOpen.value = true;
    return;
  }
  void handleButtonClick(btn);
};


const onWordAction = (action: string, card: CardData) => {
  if (action === 'trash') {
    toastAlerts.value.push({
      id: `action_${Date.now()}`,
      text: `🗑️ Moved "${card.word}" to removed words`,
      type: 'info',
      timestamp: Date.now(),
    });
    handleButtonClick({ caption: 'Next', callback_data: 'next' });
  } else if (action === 'postpone') {
    toastAlerts.value.push({
      id: `action_${Date.now()}`,
      text: `😴 Postponed "${card.word}" for later review`,
      type: 'info',
      timestamp: Date.now(),
    });
    handleButtonClick({ caption: 'Next', callback_data: 'next' });
  } else if (action === 'learned') {
    toastAlerts.value.push({
      id: `action_${Date.now()}`,
      text: `❤️ Marked "${card.word}" as learned!`,
      type: 'success',
      timestamp: Date.now(),
    });
  } else if (action === 'star_up') {
    toastAlerts.value.push({
      id: `action_${Date.now()}`,
      text: `⭐ Level increased for "${card.word}"`,
      type: 'success',
      timestamp: Date.now(),
    });
  } else if (action === 'star_down') {
    toastAlerts.value.push({
      id: `action_${Date.now()}`,
      text: `Level decreased for "${card.word}"`,
      type: 'info',
      timestamp: Date.now(),
    });
  }
};

onMounted(async () => {
  initializeTheme(currentUser.value?.user_id);
  nextTick(updateMainColumnFrame);

  mainColumnObserver = new ResizeObserver(updateMainColumnFrame);
  if (mainColumnRef.value) mainColumnObserver.observe(mainColumnRef.value);
  if (appShellRef.value) mainColumnObserver.observe(appShellRef.value);

  window.addEventListener('resize', updateMainColumnFrame);
  window.visualViewport?.addEventListener('resize', updateMainColumnFrame);

  const authenticated = await checkAuth();
  if (authenticated) {
    initSession();
  } else {
    isLoginOpen.value = true;
  }
});

watch(
  () => currentUser.value?.user_id,
  (userId) => {
    initializeTheme(userId);
  }
);

watch(isLoggedIn, (logged) => {
  if (!logged) {
    isLoginOpen.value = true;
  }
});

onBeforeUnmount(() => {
  mainColumnObserver?.disconnect();
  window.removeEventListener('resize', updateMainColumnFrame);
  window.visualViewport?.removeEventListener('resize', updateMainColumnFrame);
});
</script>
